/**
 *@desc 详情顶部
 *@author XinD
 *@date 2022/11/8
 */
import React, { Dispatch, FC, MutableRefObject, SetStateAction, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styles from "../../index.module.scss";
import { ImgAndPromptItemType } from "@/stores/gallery";
import { langStore } from "@noli/business";
import config from "@/config";
import { postOpusDel } from "@/pages/Gallery/api";
import { galleryLangRecords } from "@/pages/Gallery/lang";
import { Toast } from "@/components/Toast";
import Report from "@/pages/Gallery/components/GalleryDetail/components/Report";
import { loginStore } from "@/modules/login/store";
import { ReFreshType } from "..";

interface TopContentProps {
  EditWrapperRef: MutableRefObject<HTMLDivElement | null>;
  current?: ImgAndPromptItemType;
  opusId?: number;
  copy: (value: string, tips?: string | undefined) => void;
  onPageState: Dispatch<SetStateAction<1 | 2>>;
  onClose?: () => void;
  onRefresh?: (type: ReFreshType) => void;
  onError?: (e: any) => void;
}

const TopContent: FC<TopContentProps> = ({ ...props }) => {
  const lang = langStore.tgt;
  const {
    userState: { userInfo },
  } = loginStore;
  const { EditWrapperRef, current, opusId, onPageState, copy, onClose, onRefresh, onError } = props;
  const [delVisible, setDelVisible] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);

  // 格式化标题
  const formatTitle = useMemo(() => {
    if (!current?.opus_title) {
      return "";
    }
    const pattern = " 的 AI 创作";
    if (current.opus_title.endsWith(pattern) && lang === "en") {
      const opus_title = current.opus_title.slice(0, current.opus_title.length - pattern.length);
      return `${opus_title}'s AI art work`;
    }
    return current.opus_title;
  }, [current?.opus_title]);
  // Share Node
  const ShareNode = useMemo(() => {
    return (
      <div
        className={styles.share}
        onClick={() => {
          copy(`${location.origin}/arts/${current?.image_id}`);
        }}
      >
        <img src={`${config.buildCdnBaseUrl}assets/creator/link.png`} alt="" />
      </div>
    );
  }, [current]);
  const handleOpusDel = async () => {
    try {
      await postOpusDel({ opus_id: opusId as number, del_type: 2 });
      onClose?.();
      onRefresh?.(0);
      Toast({
        status: "success",
        position: "top",
        description: galleryLangRecords[lang]["SuccessfullyDeleted"],
      });
    } catch (e) {
      onError?.(e);
    }
  };
  const delNode = useMemo(() => {
    return (
      <button
        className={styles.routineButton}
        onClick={() => {
          setDelVisible(true);
        }}
      >
        {galleryLangRecords[lang]["Delete-Work"]}
      </button>
    );
  }, []);
  // 编辑按钮
  const editNode = useMemo(() => {
    return (
      <button
        className={styles.routineButton}
        disabled={current?.status === 2}
        onClick={(e) => {
          e.stopPropagation();
          onPageState(2);
        }}
      >
        {galleryLangRecords[lang]["EditingWorks"]}
      </button>
    );
  }, [current]);

  const reportNode = useMemo(() => {
    return (
      <div
        className={styles.reportBtnWrapper}
        onClick={() => {
          if (!loginStore.isLogin) {
            loginStore.openLoginDialog({}, async () => {
              setReportVisible(true);
            });
          } else {
            setReportVisible(true);
          }
        }}
      >
        <img src={`${config.buildCdnBaseUrl}assets/creator/report_icon.png`} />
        {galleryLangRecords[lang]["Report"]}
      </div>
    );
  }, [current]);
  // 根据opusId/current，展示相应功能按钮
  const operationButton = useMemo(() => {
    if (opusId) {
      switch (current?.status) {
        case 0:
          return (
            <>
              {delNode}
              {editNode}
              {ShareNode}
            </>
          );
        case 2:
          return (
            <>
              {delNode}
              {editNode}
            </>
          );
        case 1:
        case 3:
          return (
            <>
              {delNode}
              <button
                className={styles.routineButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onPageState(2);
                }}
              >
                {galleryLangRecords[lang]["Republish"]}
              </button>
            </>
          );
        default:
          break;
      }
    } else {
      return (
        <>
          {current?.user?.user_id !== userInfo?.userId ? reportNode : null}
          {ShareNode}
        </>
      );
    }
  }, [opusId, current]);
  // 标题右上角标签
  const titleLabel = useMemo(() => {
    switch (current?.status) {
      case 2:
        return styles.underReview;
      case 1:
      case 3:
        return styles.auditFailed;
      default:
        break;
    }
  }, [current]);

  return (
    <>
      <div className={styles.topInformation} ref={EditWrapperRef}>
        <div className={styles.leftWrapper}>
          <span className={`${styles.title} ${titleLabel}`}>{formatTitle}</span>
          <span className={styles.describe}>{current?.opus_describes}</span>
        </div>
        <div className={styles.rightWrapper}>{operationButton}</div>
      </div>
      {delVisible && (
        <div className={styles.delWrapper}>
          <p className={styles.title}>{galleryLangRecords[lang]["sureDelete"]}</p>
          <div className={styles.btnWrapper}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDelVisible(false);
              }}
            >
              {galleryLangRecords[lang]["Cancel"]}
            </button>
            <button
              onClick={async () => {
                await handleOpusDel();
              }}
            >
              {galleryLangRecords[lang]["Delete"]}
            </button>
          </div>
        </div>
      )}
      {reportVisible && (
        <Report
          reportVisible={reportVisible}
          current={current}
          setReportVisible={setReportVisible}
        />
      )}
    </>
  );
};
export default observer(TopContent);
